import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, RefreshCw, Download, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import IssuerLayout from "@/components/IssuerLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminReissued = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('issuer_token');
    if (!token) {
      navigate("/issuer/login");
      return;
    }
    
    // Check if admin
    const issuerData = JSON.parse(localStorage.getItem('issuer_data') || '{}');
    if (!issuerData.is_admin) {
      toast.error("Admin access required");
      navigate("/issuer/dashboard");
      return;
    }
    
    fetchCertificates();
  }, []);

  const fetchCertificates = async (loadMore = false, search = "") => {
    try {
      if (loadMore) {
        setLoadingMore(true);
      }
      
      const token = localStorage.getItem('issuer_token');
      const skip = loadMore ? certificates.length : 0;
      
      let url = `${API}/reissue/admin/certificates?skip=${skip}&limit=50`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (loadMore) {
        setCertificates(prev => [...prev, ...response.data.items]);
      } else {
        setCertificates(response.data.items);
      }
      
      setTotal(response.data.total);
      setHasMore(response.data.has_more);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/issuer/dashboard");
      } else {
        toast.error("Failed to load certificates");
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    fetchCertificates(false, searchTerm);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setLoading(true);
    fetchCertificates(false, "");
  };

  const exportCSV = () => {
    if (certificates.length === 0) {
      toast.error("No certificates to export");
      return;
    }

    const headers = ["Cert ID", "Student Name", "Course", "Completion Date", "Issue Date", "Email", "Created At"];
    const rows = certificates.map(cert => [
      cert.cert_id,
      cert.student_name,
      cert.course,
      cert.completion_date,
      cert.issue_date,
      cert.email,
      new Date(cert.created_at).toISOString()
    ]);

    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reissued-certificates-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("CSV exported successfully");
  };

  if (loading) {
    return (
      <IssuerLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-slate-900" />
        </div>
      </IssuerLayout>
    );
  }

  return (
    <IssuerLayout>
      <div data-testid="admin-reissued-page">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/issuer/settings")}
                className="text-slate-500 hover:text-slate-700 -ml-2"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Settings
              </Button>
            </div>
            <h1 className="text-4xl font-medium text-slate-900" style={{ fontFamily: 'Playfair Display' }}>
              Reissued Certificates
            </h1>
            <p className="text-slate-600 mt-2">
              {total} certificates issued via the reissue portal
            </p>
          </div>
          <Button
            onClick={exportCSV}
            variant="outline"
            className="border-slate-200"
            data-testid="export-csv-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Search */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by name, email, or certificate ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-10 border-slate-200"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800">
              Search
            </Button>
            {searchTerm && (
              <Button type="button" variant="outline" onClick={clearSearch}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Clear
              </Button>
            )}
          </form>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {certificates.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-slate-500">No certificates found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Cert ID</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Student Name</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Course</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Completion</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Email</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {certificates.map((cert) => (
                    <tr key={cert.id || cert.cert_id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm font-mono text-slate-900">{cert.cert_id}</td>
                      <td className="px-6 py-4 text-sm text-slate-900">{cert.student_name}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{cert.course}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{cert.completion_date}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{cert.email}</td>
                      <td className="px-6 py-4 text-sm text-slate-500">
                        {new Date(cert.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Showing {certificates.length} of {total} certificates
          </p>
          {hasMore && (
            <Button
              onClick={() => fetchCertificates(true, searchTerm)}
              disabled={loadingMore}
              variant="outline"
              className="border-slate-200"
              data-testid="load-more-btn"
            >
              {loadingMore ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Loading...
                </>
              ) : (
                `Load More (${total - certificates.length} remaining)`
              )}
            </Button>
          )}
        </div>
      </div>
    </IssuerLayout>
  );
};

export default AdminReissued;
